import {
  IconName,
  useGenericFilter,
  type ClientSideFilterRule,
  type DateRangeFilter,
  type FilterableProperty,
  type FixingIndexMarkMessageData,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { useMemo } from 'react';

export interface IndexMarksFilter extends DateRangeFilter {
  IndexID?: string[];
}

interface UseIndexMarksFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

const INDEX_MARKS_FILTER_RULES: ClientSideFilterRule<IndexMarksFilter>[] = [
  {
    type: 'include',
    filterKey: 'IndexID',
    dataKey: 'IndexID',
  },
];

export function useIndexMarksFilter({ persistedBlotterTable }: UseIndexMarksFilterParams) {
  const filterableProperties = useMemo(
    () =>
      [
        {
          key: 'IndexID',
          label: 'Index ID',
          icon: IconName.Hashtag,
          control: 'text',
        },
      ] satisfies FilterableProperty[],
    []
  );

  return useGenericFilter<IndexMarksFilter>(filterableProperties, {
    initialFilter: persistedBlotterTable.initialState?.filter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    clientSideFilterRules: INDEX_MARKS_FILTER_RULES,
  });
}

export function onlyServerFilterKeys(filter?: IndexMarksFilter) {
  if (filter == null) {
    return filter;
  }

  return pick(filter, ['StartDate', 'EndDate']);
}

const colIDToFilterBuilderKeyMap = {
  IndexID: 'IndexID',
} as const satisfies Partial<Record<keyof FixingIndexMarkMessageData, keyof IndexMarksFilter>>;

export function colIDToFilterBuilderKey(id: string): keyof IndexMarksFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof IndexMarksFilter
    | undefined;
}
